import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { api, unwrap } from '../api/client';
import { can, ME_KEY } from '../auth/session';
import { Icon } from '../components/ui/Icon';
import { setLanguage, toDbLocale } from '../i18n';
import { GlobalSearch } from '../patients/GlobalSearch';

import { setActiveBranch, useActiveBranch } from './activeBranch';

import type { Me } from '../api/client';
import type { Language } from '../i18n';

const loadAccountMenu = () => import('./AccountMenu');
const AccountMenu = lazy(async () => ({ default: (await loadAccountMenu()).AccountMenu }));

/** Sucursal elegida en la barra superior (se recuerda por navegador). */
export const branchesQuery = queryOptions({
  queryKey: ['branches'],
  queryFn: () => unwrap(api.GET('/branches')),
  staleTime: 5 * 60_000,
});

export function useBranches() {
  return useQuery(branchesQuery);
}

/** El menú de cuenta se baja al acercarse o tocarlo (RNF-PERF-05) y llega abierto. */
export function AccountMenuButton({ me }: { me: Me }) {
  const { t } = useTranslation();
  const [menuLoaded, setMenuLoaded] = useState(false);
  const accountButton = (
    <button
      type="button"
      className="icon-btn"
      aria-label={t('topbar.account')}
      aria-haspopup="menu"
      onPointerEnter={() => void loadAccountMenu()}
      onFocus={() => void loadAccountMenu()}
      onClick={() => {
        setMenuLoaded(true);
      }}
    >
      <Icon name="user" size={20} />
    </button>
  );
  return menuLoaded ? (
    <Suspense fallback={accountButton}>
      <AccountMenu me={me} />
    </Suspense>
  ) : (
    accountButton
  );
}

/** Idioma de la interfaz: cambia al instante y se guarda en el perfil. */
export function LanguageSelect() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const saveLocale = useMutation({
    mutationFn: (language: Language) =>
      unwrap(api.PATCH('/auth/me', { body: { locale: toDbLocale(language) } })),
    onSuccess: (updated) => {
      queryClient.setQueryData(ME_KEY, updated);
    },
  });
  const changeLanguage = async (language: Language) => {
    await setLanguage(language);
    saveLocale.mutate(language);
  };
  return (
    <>
      <label className="sr-only" htmlFor="language-select">
        {t('topbar.language')}
      </label>
      <select
        id="language-select"
        className="in"
        style={{ width: 'auto' }}
        value={i18n.language}
        onChange={(event) => void changeLanguage(event.target.value as Language)}
      >
        <option value="es-PY">{t('topbar.langEs')}</option>
        <option value="en-US">{t('topbar.langEn')}</option>
        <option value="pt-BR">{t('topbar.langPt')}</option>
      </select>
    </>
  );
}

/** RF-GEN-03: búsqueda, sucursal, idioma, Preguntar, Nueva cita y menú de cuenta. */
export function TopBar({ me }: { me: Me }) {
  const { t } = useTranslation();
  const location = useLocation();
  const branches = useBranches();
  const branchList = branches.data ?? [];
  const selected = useActiveBranch(branchList);

  return (
    <header className="top">
      <GlobalSearch />
      {branchList.length > 0 && (
        <>
          <label className="sr-only" htmlFor="branch-select">
            {t('topbar.branch')}
          </label>
          <select
            id="branch-select"
            className="in"
            style={{ width: 'auto' }}
            value={selected}
            onChange={(event) => {
              setActiveBranch(event.target.value);
            }}
          >
            {branchList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </>
      )}
      <div className="grow" />
      <LanguageSelect />
      {can(me, ['ai.use']) && (
        <Link className="btn ai" to="/operations/ask">
          <Icon name="ask" size={17} />
          {t('topbar.ask')}
        </Link>
      )}
      {can(me, ['agenda.write']) && (
        <Link className="btn pri" to={newAppointmentHref(location.pathname, location.search)}>
          <Icon name="plus" size={17} />
          {t('topbar.newAppointment')}
        </Link>
      )}
      <AccountMenuButton me={me} />
    </header>
  );
}

/** "Nueva cita": en la agenda conserva la fecha y la vista que se están mirando (prototipo `newCita`). */
function newAppointmentHref(pathname: string, search: string): string {
  const params = new URLSearchParams(pathname === '/agenda' ? search : '');
  params.set('new', '1');
  return `/agenda?${params.toString()}`;
}
